import { FloorAreaVerification } from '../types/protocols';

/**
 * Golvyta uppmätt på ritning (web#1383).
 *
 * A room's floor area is never stored: it is the sum of its floor components'
 * `areaShapes`. The room may carry a `floorAreaVerification` saying that sum
 * was measured on a drawing. The verification is derived to be current or
 * stale here, on both platforms with the same rule (iOS: RoomModel
 * `floorAreaVerificationState`): it is current only while no floor's
 * `areaShapes.updatedWhen` is later than `verifiedWhen`. Both are
 * `YYYY-MM-DD hh:mm:ss` in Europe/Stockholm, so a string comparison is the
 * comparison. A floor deleted after the verification is not detected.
 */

export type FloorAreaVerificationState =
    | { kind: 'none' }
    | { kind: 'verified'; verification: FloorAreaVerification }
    | { kind: 'stale'; verification: FloorAreaVerification };

/** The part of a floor component the rule reads. */
export interface FloorAreaSource {
    areaShapes?: { updatedWhen?: string | null } | null;
}

export const NONE: FloorAreaVerificationState = { kind: 'none' };

export function getFloorAreaVerificationState(
    verification: FloorAreaVerification | undefined | null,
    floors: FloorAreaSource[]
): FloorAreaVerificationState {
    if (!verification || typeof verification.verifiedWhen !== 'string' || !verification.verifiedWhen) {
        return NONE;
    }
    const changedAfter = floors.some(floor => {
        const updatedWhen = floor.areaShapes?.updatedWhen;
        return typeof updatedWhen === 'string' && updatedWhen > verification.verifiedWhen;
    });
    return changedAfter ? { kind: 'stale', verification } : { kind: 'verified', verification };
}

/** A level's Golvyta is verified only when every one of its rooms is (and it has rooms). */
export function allRoomsVerified(states: FloorAreaVerificationState[]): boolean {
    return states.length > 0 && states.every(state => state.kind === 'verified');
}

/** The `YYYY-MM-DD` part of a stored date-time. */
export const verificationDate = (verification: FloorAreaVerification): string =>
    verification.verifiedWhen.slice(0, 10);

/** Rows must add up to the total within this, after 0.1 m² rounding of each row. */
export const AREA_SPLIT_TOLERANCE = 0.05;
export const MAX_FLOOR_AREA = 1000000;

export const roundArea = (value: number): number => Math.round(value * 10) / 10;

/**
 * Spreads `total` over the floors in proportion to their current areas (an
 * equal split when none has one), each row rounded to 0.1 m² and the last
 * row taking the rounding remainder so the rows always sum to the total.
 */
export function splitAreaProportionally(total: number, currentAreas: number[]): number[] {
    const count = currentAreas.length;
    if (count === 0) return [];
    const positive = currentAreas.map(area => (Number.isFinite(area) && area > 0 ? area : 0));
    const currentSum = positive.reduce((sum, area) => sum + area, 0);
    const weights = currentSum > 0 ? positive.map(area => area / currentSum) : positive.map(() => 1 / count);

    const rows = weights.map(weight => roundArea(total * weight));
    const allButLast = rows.slice(0, -1).reduce((sum, area) => sum + area, 0);
    rows[count - 1] = roundArea(total - allButLast);
    return rows;
}

/**
 * Validates the dialog's numbers. Returns a Swedish error, or null when the
 * split can be saved. A single floor is the total itself and never fails the
 * sum check.
 */
export function validateFloorAreaSplit(total: number, rows: number[]): string | null {
    if (!Number.isFinite(total) || total <= 0) return 'Ange en golvyta större än 0 kvm';
    if (total > MAX_FLOOR_AREA) return `Maxvärde är ${MAX_FLOOR_AREA.toLocaleString('sv-SE')} kvm`;
    if (rows.length === 0) return 'Rummet har inget golv';
    if (rows.some(row => !Number.isFinite(row) || row <= 0)) return 'Varje golv måste ha en yta större än 0 kvm';
    if (rows.length === 1) return null;
    const sum = rows.reduce((acc, row) => acc + row, 0);
    if (Math.abs(sum - total) > AREA_SPLIT_TOLERANCE) {
        return `Golvens ytor (${sum.toFixed(1)} kvm) måste tillsammans bli ${total.toFixed(1)} kvm`;
    }
    return null;
}
